// This service will handle all product-related API calls
// Currently using mock data, will be replaced with actual API calls later

import axios from 'axios';
import { getAuthHeader, getCurrentUser } from './authService';
import API_CONFIG from '../config';

const API_URL = API_CONFIG.BASE_URL;
const PRODUCT_URL = `${API_URL}/products`;
const CATEGORY_URL = `${API_URL}/categories`;
const UNIT_URL = `${API_URL}/units`;
const TAX_URL = `${API_URL}/taxes`;
const FILE_UPLOAD_URL = `${API_URL}/uploads/images`;
const FILE_URL = `${API_URL}/uploads/images`;

// Mock data
const mockProducts = [
  {
    id: 1,
    name: 'Sample Product 1',
    category: 'Electronics',
    unit: 'PCS',
    costPrice: 100,
    salePrice: 150,
    quantity: 50,
    status: 'ACTIVE',
    barcode: '123456789',
  },
  // Add more mock data as needed
];

/**
 * Service for managing products
 */
const productService = {
  /**
   * Get all products with pagination
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sortField - Field to sort by
   * @param {string} sortDirection - Sort direction ('asc' or 'desc')
   * @returns {Promise} - Promise with paginated products data
   */
  getAllProducts: async (page = 0, size = 10, sortField = 'name', sortDirection = 'asc') => {
    try {
      const response = await axios.get(
        `${PRODUCT_URL}?page=${page}&size=${size}&sortField=${sortField}&direction=${sortDirection}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Get location products for current user's location
   * @param {number} categoryId - Optional category ID to filter by
   * @returns {Promise} - Promise with location products data
   */
  getLocationProducts: async (categoryId = null) => {
    try {
      let url = `${PRODUCT_URL}/location-products`;
      if (categoryId) {
        url = `${PRODUCT_URL}/location-products/category/${categoryId}`;
      }
      const response = await axios.get(
        url,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching location products:', error);
      throw error;
    }
  },

  /**
   * Get products by category ID
   * @param {number} categoryId - Category ID
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sortField - Field to sort by
   * @param {string} sortDirection - Sort direction ('asc' or 'desc')
   * @returns {Promise} - Promise with products data for the category
   */
  /** Backend GET /products/category/{id} returns a plain JSON array (not a Spring Page). */
  getProductsByCategory: async (categoryId, page = 0, size = 10, sortField = 'name', sortDirection = 'asc') => {
    try {
      const response = await axios.get(`${PRODUCT_URL}/category/${categoryId}`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error(`Error fetching products for category ID ${categoryId}:`, error);
      throw error;
    }
  },

  /**
   * Get a product by ID
   * @param {number} id - Product ID
   * @returns {Promise} - Promise with product data
   */
  getProductById: async (id) => {
    try {
      const response = await axios.get(`${PRODUCT_URL}/${id}`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error(`Error fetching product with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get a product by barcode (for barcode scan in POS / Purchase keyboard)
   * @param {string} barcode - Barcode of the product
   * @returns {Promise} - Promise with product data or null if not found
   */
  getProductByBarcode: async (barcode) => {
    if (!barcode || !String(barcode).trim()) return null;
    try {
      const user = getCurrentUser();
      const companyId = user?.companyId || user?.company_id;
      if (!companyId) {
        console.warn('No companyId available for barcode lookup');
        return null;
      }
      const response = await axios.get(
        `${PRODUCT_URL}/barcode/${encodeURIComponent(String(barcode).trim())}`,
        { params: { companyId }, headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.error(`Error fetching product by barcode:`, error);
      throw error;
    }
  },

  /**
   * Get a product by SKU (for manual SKU entry in POS / Purchase keyboard)
   * @param {string} sku - SKU of the product
   * @returns {Promise} - Promise with product data or null if not found
   */
  getProductBySku: async (sku) => {
    if (!sku || !String(sku).trim()) return null;
    try {
      const user = getCurrentUser();
      const companyId = user?.companyId || user?.company_id;
      if (!companyId) {
        console.warn('No companyId available for SKU lookup');
        return null;
      }
      const response = await axios.get(
        `${PRODUCT_URL}/sku/${encodeURIComponent(String(sku).trim())}`,
        { params: { companyId }, headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.error(`Error fetching product by SKU:`, error);
      throw error;
    }
  },

  /**
   * Create a new product
   * @param {Object} productData - Product data
   * @returns {Promise} - Promise with created product data
   */
  createProduct: async (productData) => {
    try {
      console.log('Creating product with data:', JSON.stringify(productData, null, 2));
      const response = await axios.post(PRODUCT_URL, productData, { headers: getAuthHeader() });
      console.log('Product creation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error.response?.data || error);
      console.error('Full error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        message: error.message
      });
      throw error;
    }
  },

  /**
   * Update an existing product
   * @param {number} id - Product ID
   * @param {Object} productData - Updated product data
   * @returns {Promise} - Promise with updated product data
   */
  updateProduct: async (id, productData) => {
    try {
      const response = await axios.put(`${PRODUCT_URL}/${id}`, productData, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error(`Error updating product with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a product
   * @param {number} id - Product ID
   * @returns {Promise} - Promise with deletion status
   */
  deleteProduct: async (id) => {
    try {
      const response = await axios.delete(`${PRODUCT_URL}/${id}`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error(`Error deleting product with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Upload a product image
   * @param {File} imageFile - The image file to upload
   * @returns {Promise<string>} - Promise with the image URL
   */
  uploadProductImage: async (imageFile) => {
    try {
      const formData = new FormData();
      formData.append('file', imageFile);

      const response = await axios.post(FILE_UPLOAD_URL, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!response.data || !response.data.filename) {
        throw new Error('Invalid response from server');
      }

      return response.data.filename;
    } catch (error) {
      console.error('Error uploading product image:', error.response?.data || error);
      throw error;
    }
  },

  /**
   * Get the full URL for a product image
   * @param {string} filename - The image filename
   * @returns {string} - The full URL to access the image
   */
  getProductImageUrl: (filename) => {
    if (!filename) return '/images/placeholder-product.png';
    return `${FILE_URL}/${filename}`;
  },

  /**
   * Get all product categories
   * @param {Object} params - Optional query parameters (page, size, etc.)
   * @returns {Promise} - Promise with categories data
   */
  getCategories: async (params = {}) => {
    try {
      const response = await axios.get(CATEGORY_URL, { 
        params,
        headers: getAuthHeader() 
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  /**
   * Get all units
   * @returns {Promise} - Promise with units data
   */
  getUnits: async () => {
    try {
      const response = await axios.get(UNIT_URL, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching units:', error);
      throw error;
    }
  },

  /**
   * Get all tax rates
   * @returns {Promise} - Promise with tax rates data
   */
  getTaxes: async () => {
    try {
      const response = await axios.get(TAX_URL, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching tax rates:', error);
      throw error;
    }
  },

  // Search products
  searchProducts: async (keyword) => {
    const response = await axios.get(`${PRODUCT_URL}/search`, {
      params: { keyword },
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Search products for management - includes both active and inactive products
  searchProductsForManagement: async (keyword) => {
    const response = await axios.get(`${PRODUCT_URL}/search/management`, {
      params: { keyword },
      headers: getAuthHeader()
    });
    return response.data;
  },

  // New methods for product management - includes both active and inactive products
  /**
   * Get all products for management (includes both active and inactive)
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sortField - Field to sort by
   * @param {string} sortDirection - Sort direction ('asc' or 'desc')
   * @returns {Promise} - Promise with paginated products data
   */
  getAllProductsForManagement: async (page = 0, size = 10, sortField = 'name', sortDirection = 'asc') => {
    try {
      const response = await axios.get(
        `${PRODUCT_URL}/management?page=${page}&size=${size}&sort=${sortField}&direction=${sortDirection}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching products for management:', error);
      throw error;
    }
  },

  /**
   * Get products by category for management (includes both active and inactive)
   * @param {number} categoryId - Category ID
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sortField - Field to sort by
   * @param {string} sortDirection - Sort direction ('asc' or 'desc')
   * @returns {Promise} - Promise with products data for the category
   */
  getProductsByCategoryForManagement: async (categoryId, page = 0, size = 10, sortField = 'name', sortDirection = 'asc') => {
    try {
      const response = await axios.get(
        `${PRODUCT_URL}/category/${categoryId}/management/paged?page=${page}&size=${size}&sortField=${sortField}&direction=${sortDirection}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching products for category ID ${categoryId} for management:`, error);
      throw error;
    }
  },

  /**
   * Get repacked variants for a parent product
   * @param {number} parentId - Parent product ID
   * @returns {Promise<Array>} List of repacked product variants
   */
  getRepackedVariants: async (parentId) => {
    try {
      const response = await axios.get(`${PRODUCT_URL}/${parentId}/repacked-variants`, {
        headers: getAuthHeader()
      });
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching repacked variants for product ${parentId}:`, error);
      throw error;
    }
  },

  /**
   * Create a repacked variant for a parent product
   * @param {number} parentId - Parent product ID
   * @param {Object} data - Repacked variant data
   * @returns {Promise<Object>} Created repacked product
   */
  createRepackedVariant: async (parentId, data) => {
    try {
      const response = await axios.post(`${PRODUCT_URL}/${parentId}/repacked-variants`, data, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating repacked variant:', error.response?.data || error);
      throw error;
    }
  },

  /**
   * Update an existing repacked variant
   * @param {number} variantId - Repacked variant ID
   * @param {Object} data - Updated variant data
   * @returns {Promise<Object>} Updated repacked product
   */
  updateRepackedVariant: async (variantId, data) => {
    try {
      const response = await axios.put(`${PRODUCT_URL}/repacked-variants/${variantId}`, data, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating repacked variant ${variantId}:`, error.response?.data || error);
      throw error;
    }
  }
};

export default productService;